import { styled, useBreakpointIndex } from 'dripsy';
import React, { Fragment, useCallback, useMemo, useRef } from 'react';
import { View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Video } from 'expo-av';
import { useModal } from 'react-native-modalfy';

import { HubNavigatorRoutePropType } from '@navigation';
import { useAsyncInit, useNavOptionsSet } from '@hooks';
import { TutorialContent } from './components/TutorialContent';
import { TutorialPlaylist } from './components/TutorialPlaylist';
import {
  ScreenLoader,
  Separator,
  ScreenErrorView,
} from '@components';
import { IS_WEB, parseApiError } from '@utils';
import { CommentsList } from './components/CommentsList';
import { InfoPanel } from './components/InfoPanel';
import { TutorialPlaylistHeader } from './components/TutorialPlaylistHeader';
import { useTutorialsModel } from '@models/Tutorials/TutorialsModel';
import { ToastService } from '@services';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import { SideList } from './components/SideList';
import { TutorialTimestamp } from '@models/Tutorials/TutorialTimestampModel';
import { modals, ModalStackParamsType } from '@modals';
import { CoursePlaylist } from './components/CoursePlaylist';
import { CoursePlaylistHeader } from './components/CoursePlaylistHeader';
import { useCourseCollectionModel } from '@models/Courses/CourseCollectionModel';
import { CourseLessonOverview } from './components/CourseLessonOverview';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

const ContentContainer = styled(View)({
  flex: 1,
  mx: ['$0', '$0', '5%'],
  flexDirection: ['column', 'row'],
});

const CommentsListContainer = styled(View)({
  flex: 3,
});

const SideListContainer = styled(View)({
  flex: 1,
});

type NavigationRoutePropType = HubNavigatorRoutePropType<'Tutorial'>;

export interface ITutorialScreenStore {
  isShowTimestamps: boolean;
  toggleIsShowTimestamps(): void;
}

export const TutorialScreen = observer(() => {
  const { params } = useRoute<NavigationRoutePropType>();
  const tutorials = useTutorialsModel();
  const courses = useCourseCollectionModel();
  const item = tutorials.getById(params.id);
  const course = params.courseId
    ? courses.getById(params.courseId)
    : undefined;

  const bIndex = useBreakpointIndex();
  const videoPlayerRef = useRef<Video>(null);
  const fullScreenVideoPlayerRef = useRef<Video>(null);
  const modal = useModal<ModalStackParamsType>();
  const store = useLocalObservable<ITutorialScreenStore>(() => ({
    isShowTimestamps: false,

    toggleIsShowTimestamps() {
      this.isShowTimestamps = !this.isShowTimestamps;
    },
  }));

  const lessonName = course?.lessons?.getLessonByTutorialId(
    params.id,
  )?.name;

  const headerTitle = useMemo(() => {
    if (course) {
      return IS_WEB ? `${lessonName}: ${item?.title}` : lessonName;
    }

    return item?.title;
  }, [lessonName, item, course]);

  useNavOptionsSet({ title: headerTitle }, [headerTitle]);

  useAsyncInit(async () => {
    try {
      if (!course && params.courseId) {
        await courses.fetchById.run(params.courseId);
      }
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }, [params.courseId]);

  useAsyncInit(async () => {
    try {
      await tutorials.fetchById.run(params.id);
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }, [params.id]);

  useAsyncInit(async () => {
    try {
      await item?.fetchTimestamps.run();
    } catch (error) {
      // Do nothing
    }
  }, [item?.id]);

  useAsyncInit(async () => {
    try {
      if (course && !course.lessons.fetch.inProgress) {
        await course?.lessons.fetch.run();
      }
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }, [course]);

  const toggleIsShowTimestamps = useCallback(() => {
    if (IS_WEB) {
      store.toggleIsShowTimestamps();
    } else {
      modal.openModal(modals.TimestampsModal, {
        tutorial: item as Tutorial,
        onSelect: selectTimestamp,
      });
    }
  }, []);

  const ContentHeaderComponent = useCallback(() => {
    const playlist = course
      ? course.lessons.playlist
      : tutorials.related;

    return (
      <Fragment>
        <TutorialContent
          tutorial={item as Tutorial}
          course={course}
          playlist={playlist}
          onPressTimestamp={toggleIsShowTimestamps}
          videoPlayerRef={videoPlayerRef}
          fullScreenVideoPlayerRef={fullScreenVideoPlayerRef}
        />
        <Separator size={['$7', '$8']} />
        <InfoPanel tutorial={item as Tutorial} course={course} />
        {bIndex > 0 && IS_WEB && course && (
          <Fragment>
            <Separator size="$9" />
            <CourseLessonOverview
              course={course}
              tutorial={item as Tutorial}
            />
            <Separator size="$9" />
          </Fragment>
        )}
        {bIndex === 0 && (
          <Fragment>
            {course ? (
              <Fragment>
                <Separator size="$7" />
                <CourseLessonOverview
                  course={course}
                  tutorial={item as Tutorial}
                />
                <Separator size="$9" />
                <CoursePlaylistHeader course={course} />
              </Fragment>
            ) : (
              <Fragment>
                <Separator size="$8" />
                <TutorialPlaylistHeader />
              </Fragment>
            )}
          </Fragment>
        )}
        <Separator />
      </Fragment>
    );
  }, [item, course]);

  const Playlist = useCallback(() => {
    if (course) {
      return (
        <CoursePlaylist
          ListHeaderComponent={ContentHeaderComponent}
          course={course}
        />
      );
    }

    return (
      <TutorialPlaylist
        ListHeaderComponent={ContentHeaderComponent}
        tutorial={item as Tutorial}
      />
    );
  }, [course, item, ContentHeaderComponent]);

  const selectTimestamp = useCallback(
    (timestamp: TutorialTimestamp) => {
      const newPositionTolerance = {
        toleranceMillisBefore: 0,
        toleranceMillisAfter: 0,
      };

      videoPlayerRef.current?.setPositionAsync(
        timestamp.time,
        newPositionTolerance,
      );
      fullScreenVideoPlayerRef.current?.setPositionAsync(
        timestamp.time,
        newPositionTolerance,
      );
    },
    [item],
  );

  if (tutorials.fetchById.isError) {
    return <ScreenErrorView error={tutorials.fetchById.error} />;
  }

  if (!item || (tutorials.fetchById.inProgress && !item)) {
    return <ScreenLoader />;
  }

  return (
    <Container>
      <ContentContainer>
        {bIndex > 0 && IS_WEB ? (
          <Fragment>
            <CommentsListContainer>
              <CommentsList
                tutorial={item}
                HeaderComponent={ContentHeaderComponent}
              />
            </CommentsListContainer>
            <Separator size="$8" vertical />
            <SideListContainer>
              <SideList
                tutorial={item}
                course={course}
                screenStore={store}
                onSelectTimestamp={selectTimestamp}
              />
            </SideListContainer>
          </Fragment>
        ) : (
          <Playlist />
        )}
      </ContentContainer>
    </Container>
  );
});
